import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  ProjectResource,
  syncAndListProjectResources,
  addPoolMemberToProject,
  removeProjectResource,
} from '../../../services/projectResourceService';
import { ResourcePoolMember } from '../../../services/resourcePoolService';
import { GroupMember } from '../../../services/groupService';

interface Props {
  pca: IPublicClientApplication;
  projectId: string;
  poolId: string;
  groupId: string | null;
}

/**
 * Resources roster for a pool-linked project. On open (and on the manual "Sync
 * Group Members" button) it cross-checks the project's M365 Group against the
 * tenant Resource Pool and additively links matches; rate / hours / role are
 * shown LIVE from the pool member (never cached). Non-pool group members are
 * flagged, not added. Only pool members can ever be resources.
 */
export default function ProjectResourcesSection({ pca, projectId, poolId, groupId }: Props) {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resources, setResources] = useState<ProjectResource[]>([]);
  const [nonPool, setNonPool] = useState<GroupMember[]>([]);
  const [poolMembers, setPoolMembers] = useState<ResourcePoolMember[]>([]);
  const [showNonPool, setShowNonPool] = useState(false);
  const [pickSel, setPickSel] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const runSync = useCallback(async (manual: boolean) => {
    if (manual) setSyncing(true); else setLoading(true);
    setError(null);
    try {
      const r = await syncAndListProjectResources(pca, projectId, poolId, groupId);
      setResources(r.resources);
      setNonPool(r.nonPoolGroupMembers);
      setPoolMembers(r.poolMembers);
      if (manual) {
        setSyncMsg(r.added > 0 ? `Synced — added ${r.added} pool member${r.added === 1 ? '' : 's'} from the group.` : 'Synced — no new pool members to add.');
        setTimeout(() => setSyncMsg(null), 4000);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load resources.');
    } finally {
      setSyncing(false);
      setLoading(false);
    }
  }, [pca, projectId, poolId, groupId]);

  useEffect(() => { runSync(false); }, [runSync]);

  const availableToAdd = poolMembers
    .filter(m => !resources.some(r => r.poolMemberId === m.dagrun_pm_resourcepoolmemberid))
    .sort((a, b) => (a.dagrun_pm_username ?? '').localeCompare(b.dagrun_pm_username ?? ''));

  const add = async () => {
    const m = poolMembers.find(x => x.dagrun_pm_resourcepoolmemberid === pickSel);
    if (!m || addBusy) return;
    setAddBusy(true);
    setError(null);
    try {
      await addPoolMemberToProject(pca, projectId, m);
      setPickSel('');
      await runSync(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add resource.');
    } finally {
      setAddBusy(false);
    }
  };

  const remove = async (id: string) => {
    const prev = resources;
    setResources(p => p.filter(r => r.id !== id)); // optimistic
    try { await removeProjectResource(pca, id); }
    catch { setResources(prev); setError('Failed to remove resource.'); }
  };

  const fmtRate = (r: number | null) => (r == null ? '—' : `$${r.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr`);
  const fmtHours = (h: number | null) => (h == null ? '—' : `${h}h/day`);

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="eyebrow" style={{ margin: 0 }}>PROJECT RESOURCES</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {syncMsg && <span style={{ fontSize: 12, color: 'var(--brand)' }}>{syncMsg}</span>}
          <button className="btn-secondary" onClick={() => runSync(true)} disabled={syncing || loading}>
            {syncing ? 'Syncing…' : 'Sync Group Members'}
          </button>
        </div>
      </div>

      <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Resources are tenant Resource Pool members. Group members in the pool are added automatically;
        rate and hours/day are read live from the pool. Removal here is manual and never automatic.
      </p>

      {error && <div style={{ fontSize: 12, color: '#DC2626', marginBottom: 10 }}>{error}</div>}

      {/* Non-pool group members flag */}
      {nonPool.length > 0 && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 'var(--r-md)', padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#92400E' }}>
              <strong>{nonPool.length}</strong> group member{nonPool.length === 1 ? '' : 's'} {nonPool.length === 1 ? 'is' : 'are'} not in the Resource Pool and {nonPool.length === 1 ? 'was' : 'were'} not added.
            </span>
            <button
              type="button"
              onClick={() => setShowNonPool(s => !s)}
              style={{ background: 'transparent', border: 'none', color: '#92400E', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
            >
              {showNonPool ? 'Hide' : 'See who'}
            </button>
          </div>
          {showNonPool && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: '#78350F' }}>
              {nonPool.map(m => (
                <li key={m.id}>{m.displayName}{m.mail || m.userPrincipalName ? ` — ${m.mail || m.userPrincipalName}` : ''}</li>
              ))}
            </ul>
          )}
          <div style={{ fontSize: 11, color: '#92400E', marginTop: 6 }}>
            Add them to the tenant pool in Admin if they should be resources, then Sync.
          </div>
        </div>
      )}

      {/* Resource list */}
      {loading ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading resources…</div>
      ) : resources.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          No resources yet. Group members who are in the Resource Pool are added on sync, or add pool members below.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--ink-4)', fontSize: 11, textTransform: 'uppercase' }}>
              <th style={{ padding: '6px 8px' }}>Name</th>
              <th style={{ padding: '6px 8px' }}>Role</th>
              <th style={{ padding: '6px 8px' }}>Hours/day</th>
              <th style={{ padding: '6px 8px' }}>Rate</th>
              <th style={{ padding: '6px 8px' }}>Source</th>
              <th style={{ padding: '6px 8px' }} />
            </tr>
          </thead>
          <tbody>
            {resources.map(r => (
              <tr key={r.id} style={{ borderTop: '0.5px solid var(--border-light)' }}>
                <td style={{ padding: '8px 8px', color: 'var(--ink)' }}>
                  {r.name}
                  {r.poolMemberMissing && <span style={{ marginLeft: 6, fontSize: 11, color: '#DC2626' }}>(removed from pool)</span>}
                </td>
                <td style={{ padding: '8px 8px', color: 'var(--ink-2)' }}>{r.role || '—'}</td>
                <td style={{ padding: '8px 8px', color: 'var(--ink-2)' }}>{fmtHours(r.hoursPerDay)}</td>
                <td style={{ padding: '8px 8px', color: 'var(--ink-2)' }}>{fmtRate(r.hourlyRate)}</td>
                <td style={{ padding: '8px 8px' }}>
                  <span style={{
                    fontSize: 11, padding: '2px 7px', borderRadius: 999,
                    background: r.addedVia === 'GroupSync' ? 'var(--brand-mist)' : 'var(--surface-2)',
                    color: r.addedVia === 'GroupSync' ? 'var(--brand-dark)' : 'var(--ink-3)',
                  }}>
                    {r.addedVia === 'GroupSync' ? 'via Group' : 'Manually added'}
                  </span>
                </td>
                <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                  <button
                    type="button"
                    title="Remove resource (manual — does not affect the group or pool)"
                    onClick={() => remove(r.id)}
                    style={{ background: 'transparent', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 15, lineHeight: 1 }}
                  >×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add from Resource Pool */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
        <select
          value={pickSel}
          onChange={e => setPickSel(e.target.value)}
          disabled={addBusy || availableToAdd.length === 0}
          style={{ flex: 1, padding: '8px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--surface)', color: 'var(--ink)' }}
        >
          <option value="">{availableToAdd.length === 0 ? 'All pool members are already on this project' : 'Add from Resource Pool…'}</option>
          {availableToAdd.map(m => (
            <option key={m.dagrun_pm_resourcepoolmemberid} value={m.dagrun_pm_resourcepoolmemberid}>
              {m.dagrun_pm_username}{m.dagrun_pm_role ? ` — ${m.dagrun_pm_role}` : ''}
            </option>
          ))}
        </select>
        <button className="btn-primary" onClick={add} disabled={!pickSel || addBusy}>
          {addBusy ? 'Adding…' : 'Add'}
        </button>
      </div>
    </div>
  );
}
